import React, { useState, useMemo, useCallback } from 'react';
import { useShallowEqualSelector } from '../../../../../hooks/redux';

import { ReduxState } from '../../../../../redux/reducers';
import { SettingsModelTypeSchool } from '../../../../../interfaces/merchants/resources/books';
import { getMerchantResourcesBooks } from '../../../../../redux/selectors/merchants/resources/books';
import {
  goTo,
  nextStep,
  finalStep,
} from '../../../../../redux/actions/merchants/resources/books';

import {
  StepItems,
  BookStepSettingPair,
} from '../../../../../molecules/merchant/resources/book/BooksStep';

import BookingWithPageDetail from '../BookingWithPageDetail';
import ReservationFlowAuthPage from '../ReservationFlowAuthPage';
import { InputPaymentOrganism } from '../InputPayment';
import InputBookingTargetLesson from '../InputBookingTargetLesson';
import InputCompletionFailed from '../InputCompletionFailed';
import InputCompletionSchool from '../InputCompletionSchool';
import InputConfirmationSchool from '../InputConfirmationSchool';
import InputSchoolCalendar from '../InputSchoolCalendar';
import InputUserInfo from '../InputUserInfo';
import { BooksStepOrganism } from '../../../../reservation-flow/BooksStep';
import { useAppDispatch } from '../../../../../redux';

interface Props {
  isExpressFlow: boolean;
  merchantId: string;
  resourcePublicId: string;
  lineLiffId: string | undefined;
}

const FULL_WORKFLOW_STEPS = [
  StepItems.Booking,
  StepItems.SelectDate,
  StepItems.Customer,
  StepItems.Payment,
  StepItems.Confirmation,
];
const SKIP_PAYMENT_WORKFLOW_STEPS = [
  StepItems.Booking,
  StepItems.SelectDate,
  StepItems.Customer,
  StepItems.Confirmation,
];
const ERROR_REINPUT_PAYMENT_STEP = 97;
const ERROR_RESTART_STEP = 98;
const ERROR_RESELECT_STEP = 99;

const ModelTypeSchool: React.FC<Props> = ({
  isExpressFlow,
  merchantId,
  resourcePublicId,
  lineLiffId,
}) => {
  const dispatch = useAppDispatch();
  const [localRenew, setLocalRenew] = useState<boolean>(false);

  const merchantResourcesBooks = useShallowEqualSelector((state: ReduxState) =>
    getMerchantResourcesBooks(state),
  );
  const { merchantResource, step, postable } = merchantResourcesBooks;

  const settings = useMemo<SettingsModelTypeSchool>(
    () => merchantResourcesBooks.settings as SettingsModelTypeSchool,
    [merchantResourcesBooks],
  );
  const publicId = useMemo(
    () => merchantResourcesBooks.merchantId!,
    [merchantResourcesBooks],
  );

  const handleBackToModifyPage = useCallback(
    (step: number) => {
      setLocalRenew(true);
      dispatch(goTo(step));
    },
    [dispatch],
  );

  const renderRequireLoginAt = useCallback(
    (step: number) => {
      return (
        <ReservationFlowAuthPage
          moveToNext={() => dispatch(nextStep(step))}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
          resourcePublicId={resourcePublicId}
        />
      );
    },
    [dispatch, resourcePublicId],
  );

  const renderInputBookingTargetAt = useCallback(
    (step: number) => {
      if (isExpressFlow) {
        return (
          <BookingWithPageDetail
            merchantId={merchantId}
            merchantResource={merchantResource!}
            showPerformableStaffMembers={false}
          >
            <InputBookingTargetLesson
              modelType={settings.modelType}
              renew={localRenew}
              settings={settings.bookingTargets}
              postable={postable!}
              onSubmit={() => dispatch(nextStep(step))}
              onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
              withExpressFlow={isExpressFlow}
              resourcePublicId={resourcePublicId}
            />
          </BookingWithPageDetail>
        );
      } else {
        return (
          <InputBookingTargetLesson
            modelType={settings.modelType}
            renew={localRenew}
            settings={settings.bookingTargets}
            postable={postable!}
            onSubmit={() => dispatch(nextStep(step))}
            onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
            withExpressFlow={isExpressFlow}
            resourcePublicId={resourcePublicId}
          />
        );
      }
    },
    [
      dispatch,
      isExpressFlow,
      localRenew,
      merchantId,
      merchantResource,
      postable,
      resourcePublicId,
      settings,
    ],
  );

  const renderInputBookingDateTimeAt = useCallback(
    (step: number, modifierStep: number) => {
      return (
        <InputSchoolCalendar
          settings={settings.bookingDateTimePicker}
          postable={postable!}
          onSubmit={() => dispatch(nextStep(step))}
          backToModifyPage={() => handleBackToModifyPage(modifierStep)}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
          resourcePublicId={resourcePublicId}
        />
      );
    },
    [dispatch, settings, postable, resourcePublicId, handleBackToModifyPage],
  );

  const renderInputUserInfoAt = useCallback(
    (step: number, submitLabelIntlId: string) => {
      return (
        <InputUserInfo
          settings={settings.userInfo}
          onSubmit={() => dispatch(nextStep(step))}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
          submitLabelIntlId={submitLabelIntlId}
          merchantPublicId={merchantId}
          resourcePublicId={resourcePublicId}
        />
      );
    },
    [dispatch, settings, merchantId, resourcePublicId],
  );

  const renderInputPaymentAt = useCallback(
    (step: number) => {
      return (
        <InputPaymentOrganism
          settings={settings.payment!}
          merchantPublicId={publicId}
          postable={postable!}
          onSubmit={() => dispatch(nextStep(step))}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
        />
      );
    },
    [dispatch, settings, publicId, postable],
  );

  const renderInputConfirmationAt = useCallback(
    (step: number) => {
      return (
        <InputConfirmationSchool
          merchantResource={{
            publicId: merchantResource!.publicId,
            name: merchantResource!.name,
            imageUrl: merchantResource!.primaryImage?.thumb?.url,
          }}
          settings={settings.confirmation}
          postable={postable!}
          onSubmit={() => dispatch(finalStep(step))}
          onReinputPayment={() => dispatch(goTo(ERROR_REINPUT_PAYMENT_STEP))}
          onReselect={() => dispatch(goTo(ERROR_RESELECT_STEP))}
          onRestart={() => dispatch(goTo(ERROR_RESTART_STEP))}
          publicId={publicId}
          lineLiffId={lineLiffId}
        />
      );
    },
    [dispatch, settings, merchantResource, postable, publicId, lineLiffId],
  );

  const renderInputCompletionSchool = useCallback(() => {
    return (
      <InputCompletionSchool
        merchantResource={{
          publicId: merchantResource!.publicId,
          name: merchantResource!.name,
          imageUrl: merchantResource!.primaryImage?.thumb?.url,
        }}
        settings={settings.completion!}
        publicId={publicId}
        withExpressFlow={isExpressFlow}
        resourcePublicId={resourcePublicId}
      />
    );
  }, [isExpressFlow, merchantResource, publicId, resourcePublicId, settings]);

  const renderFailedRestart = useCallback(() => {
    return (
      <InputCompletionFailed
        settings={settings.completion!}
        backToLabel={'books.submitLabel.restart'}
        backToModifyPage={() => location.reload()}
      />
    );
  }, [settings]);

  const renderFailedReselect = useCallback(
    (modifierStep: number) => {
      return (
        <InputCompletionFailed
          settings={settings.completion!}
          backToLabel={'books.submitLabel.reselect'}
          backToModifyPage={() => dispatch(goTo(modifierStep))}
        />
      );
    },
    [settings, dispatch],
  );

  const renderFailedReinput = useCallback(
    (modifierStep: number) => {
      return (
        <InputCompletionFailed
          settings={settings.completion!}
          backToLabel={'books.submitLabel.reinput'}
          backToModifyPage={() => dispatch(goTo(modifierStep))}
        />
      );
    },
    [settings, dispatch],
  );

  const fullWorkFlowSetting = useCallback((): BookStepSettingPair => {
    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => renderRequireLoginAt(0)},
      1: { displayProgress: true,  currentProgress: StepItems.Booking,      renderFn: () => renderInputBookingTargetAt(1)   },
      2: { displayProgress: true,  currentProgress: StepItems.SelectDate,   renderFn: () => renderInputBookingDateTimeAt(2, 1) },
      3: { displayProgress: true,  currentProgress: StepItems.Customer,     renderFn: () => renderInputUserInfoAt(3, 'books.submitLabel.payment') },
      4: { displayProgress: true,  currentProgress: StepItems.Payment,      renderFn: () => renderInputPaymentAt(4)         },
      5: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => renderInputConfirmationAt(5)    },
      6: { displayProgress: false, currentProgress: null,                   renderFn: () => renderInputCompletionSchool()         },

      [ERROR_REINPUT_PAYMENT_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedReinput(4) },
      [ERROR_RESTART_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedRestart() },
      [ERROR_RESELECT_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedReselect(2) },
    }[step!];

    return [FULL_WORKFLOW_STEPS, setting];
  }, [
    renderFailedReinput,
    renderFailedReselect,
    renderFailedRestart,
    renderInputBookingDateTimeAt,
    renderInputBookingTargetAt,
    renderInputCompletionSchool,
    renderInputConfirmationAt,
    renderInputPaymentAt,
    renderInputUserInfoAt,
    renderRequireLoginAt,
    step,
  ]);

  const skipPaymentFlowSetting = useCallback((): BookStepSettingPair => {
    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => renderRequireLoginAt(0)},
      1: { displayProgress: true,  currentProgress: StepItems.Booking,      renderFn: () => renderInputBookingTargetAt(1)   },
      2: { displayProgress: true,  currentProgress: StepItems.SelectDate,   renderFn: () => renderInputBookingDateTimeAt(2, 1) },
      3: { displayProgress: true,  currentProgress: StepItems.Customer,     renderFn: () => renderInputUserInfoAt(3, 'books.submitLabel.confirm') },
      4: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => renderInputConfirmationAt(4)    },
      5: { displayProgress: false, currentProgress: null,                   renderFn: () => renderInputCompletionSchool()         },

      [ERROR_RESTART_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedRestart() },
      [ERROR_RESELECT_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => renderFailedReselect(2) },
    }[step!];

    return [SKIP_PAYMENT_WORKFLOW_STEPS, setting];
  }, [
    renderFailedReselect,
    renderFailedRestart,
    renderInputBookingDateTimeAt,
    renderInputBookingTargetAt,
    renderInputCompletionSchool,
    renderInputConfirmationAt,
    renderInputUserInfoAt,
    renderRequireLoginAt,
    step,
  ]);

  const [allSteps, setting] = merchantResource!.paymentStep
    ? fullWorkFlowSetting()
    : skipPaymentFlowSetting();
  return (
    <>
      {setting && setting.currentProgress && setting.displayProgress && (
        <BooksStepOrganism
          allSteps={allSteps}
          current={allSteps.indexOf(setting.currentProgress) + 1}
          resourcePublicId={merchantResource?.publicId ?? ''}
        />
      )}
      {setting && setting.renderFn()}
    </>
  );
};

export default ModelTypeSchool;
